import React from 'react'
import { Suggestion } from '../../types/mockup'
import './ImpactAnalysis.css'

interface ImpactAnalysisProps {
  suggestion: Suggestion
}

export const ImpactAnalysis: React.FC<ImpactAnalysisProps> = ({ suggestion }) => {
  // Format currency
  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0
    }).format(amount)
  }

  // Format percentage
  const formatPercentage = (value: number): string => {
    return `${value}%`
  }

  // Format time duration
  const formatDuration = (minutes: number): string => {
    if (minutes < 60) return `${minutes}min`
    const hours = Math.floor(minutes / 60)
    const remainingMinutes = minutes % 60
    return remainingMinutes > 0 ? `${hours}h ${remainingMinutes}min` : `${hours}h`
  }

  // Get stress level color
  const getStressLevelColor = (level: number): string => {
    if (level <= 3) return '#4caf50'
    if (level <= 6) return '#ff9800'
    return '#f44336'
  }

  return (
    <div className="impact-analysis">
      {/* Layer 1: What - Factual Impact */}
      <div className="impact-section">
        <h3 className="section-title">
          <span className="section-number">1</span>
          What's Changing
        </h3>
        <div className="section-content">
          <div className="factual-grid">
            {/* Schedule Changes */}
            <div className="impact-card">
              <div className="card-header">
                <span className="card-icon">📅</span>
                <h4 className="card-title">Schedule Impact</h4>
              </div>
              <div className="card-content">
                {suggestion.operationalImpact.timelineChanges.length > 0 ? (
                  <ul className="change-list">
                    {suggestion.operationalImpact.timelineChanges.slice(0, 3).map((change, index) => (
                      <li key={index} className="change-item">
                        <strong>{change.taskName}</strong>
                        <span className="time-change">
                          {change.oldTime} → {change.newTime}
                        </span>
                        <span className={`duration-change ${change.durationDelta > 0 ? 'increase' : 'decrease'}`}>
                          {change.durationDelta > 0 ? '+' : ''}{change.durationDelta}min
                        </span>
                      </li>
                    ))}
                    {suggestion.operationalImpact.timelineChanges.length > 3 && (
                      <li className="change-item more-changes">
                        +{suggestion.operationalImpact.timelineChanges.length - 3} more changes
                      </li>
                    )}
                  </ul>
                ) : (
                  <p className="no-changes">No schedule changes required</p>
                )}
              </div>
            </div>

            {/* Resource Impact */}
            <div className="impact-card">
              <div className="card-header">
                <span className="card-icon">👥</span>
                <h4 className="card-title">Resource Impact</h4>
              </div>
              <div className="card-content">
                <div className="resource-stats">
                  <div className="stat-item">
                    <span className="stat-value">{suggestion.operationalImpact.affectedVendors.length}</span>
                    <span className="stat-label">Vendors Affected</span>
                  </div>
                  {suggestion.impact.guestsAffected && (
                    <div className="stat-item">
                      <span className="stat-value">{suggestion.impact.guestsAffected}</span>
                      <span className="stat-label">Guests Affected</span>
                    </div>
                  )}
                  <div className="stat-item">
                    <span className="stat-value">{suggestion.operationalImpact.timelineChanges.length}</span>
                    <span className="stat-label">Tasks Modified</span>
                  </div>
                </div>
                {suggestion.operationalImpact.affectedVendors.length > 0 && (
                  <div className="vendor-list">
                    <h5>Affected Vendors:</h5>
                    <div className="vendor-chips">
                      {suggestion.operationalImpact.affectedVendors.map((vendor, index) => (
                        <span key={index} className="vendor-chip">{vendor}</span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Layer 2: So What - Operational Impact */}
      <div className="impact-section">
        <h3 className="section-title">
          <span className="section-number">2</span>
          So What - Operational Impact
        </h3>
        <div className="section-content">
          <div className="operational-grid">
            {/* Financial Impact */}
            {suggestion.operationalImpact.financialImpact && (
              <div className="impact-card">
                <div className="card-header">
                  <span className="card-icon">💰</span>
                  <h4 className="card-title">Financial Impact</h4>
                </div>
                <div className="card-content">
                  <div className="financial-breakdown">
                    {suggestion.operationalImpact.financialImpact.additionalCosts > 0 && (
                      <div className="financial-item cost">
                        <span className="label">Additional Costs:</span>
                        <span className="value">
                          {formatCurrency(suggestion.operationalImpact.financialImpact.additionalCosts)}
                        </span>
                      </div>
                    )}
                    {suggestion.operationalImpact.financialImpact.potentialSavings > 0 && (
                      <div className="financial-item savings">
                        <span className="label">Potential Savings:</span>
                        <span className="value">
                          {formatCurrency(suggestion.operationalImpact.financialImpact.potentialSavings)}
                        </span>
                      </div>
                    )}
                    {suggestion.operationalImpact.financialImpact.riskOfOverruns > 0 && (
                      <div className="financial-item risk">
                        <span className="label">Risk of Cost Overruns:</span>
                        <span className="value">
                          {formatPercentage(suggestion.operationalImpact.financialImpact.riskOfOverruns)}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Coordination Complexity */}
            <div className="impact-card">
              <div className="card-header">
                <span className="card-icon">🔄</span>
                <h4 className="card-title">Coordination Complexity</h4>
              </div>
              <div className="card-content">
                <div className="complexity-metrics">
                  <div className="metric-item">
                    <span className="metric-label">Coordination Difficulty:</span>
                    <div className="difficulty-bar">
                      <div 
                        className="difficulty-fill"
                        style={{ 
                          width: `${suggestion.operationalImpact.coordinationComplexity * 10}%`,
                          backgroundColor: suggestion.operationalImpact.coordinationComplexity <= 3 ? '#4caf50' : 
                                         suggestion.operationalImpact.coordinationComplexity <= 6 ? '#ff9800' : '#f44336'
                        }}
                      />
                    </div>
                    <span className="metric-value">{suggestion.operationalImpact.coordinationComplexity}/10</span>
                  </div>
                  
                  <div className="metric-item">
                    <span className="metric-label">Implementation Time:</span>
                    <span className="metric-value">
                      {formatDuration(suggestion.operationalImpact.implementationTimeMinutes)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Layer 3: Now What - Experience Impact */}
      <div className="impact-section">
        <h3 className="section-title">
          <span className="section-number">3</span>
          Now What - Human Impact
        </h3>
        <div className="section-content">
          <div className="experience-grid">
            {/* Guest Experience */}
            <div className="impact-card">
              <div className="card-header">
                <span className="card-icon">🎉</span>
                <h4 className="card-title">Guest Experience</h4>
              </div>
              <div className="card-content">
                <div className="experience-metrics">
                  <div className="metric-row">
                    <span className="metric-label">Satisfaction Impact:</span>
                    <span className={`metric-value ${suggestion.experienceImpact.guestSatisfactionDelta >= 0 ? 'positive' : 'negative'}`}>
                      {suggestion.experienceImpact.guestSatisfactionDelta >= 0 ? '+' : ''}
                      {suggestion.experienceImpact.guestSatisfactionDelta}%
                    </span>
                  </div>
                  
                  <div className="metric-row">
                    <span className="metric-label">Probability Guests Notice:</span>
                    <span className="metric-value">
                      {formatPercentage(suggestion.experienceImpact.probabilityOfNotice)}
                    </span>
                  </div>
                  
                  <div className="risk-description">
                    <h5>Risk Assessment:</h5>
                    <p>{suggestion.experienceImpact.riskDescription}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Team Stress */}
            <div className="impact-card">
              <div className="card-header">
                <span className="card-icon">😰</span>
                <h4 className="card-title">Team Stress</h4>
              </div>
              <div className="card-content">
                <div className="stress-breakdown">
                  {Object.entries(suggestion.experienceImpact.teamStressLevels).map(([team, level]) => (
                    <div key={team} className="stress-item">
                      <span className="team-name">{team}:</span>
                      <div className="stress-bar">
                        <div 
                          className="stress-fill"
                          style={{ 
                            width: `${level * 10}%`,
                            backgroundColor: getStressLevelColor(level)
                          }}
                        />
                      </div>
                      <span className="stress-value">{level}/10</span>
                    </div>
                  ))}
                </div>
                
                {suggestion.experienceImpact.mitigationStrategies && suggestion.experienceImpact.mitigationStrategies.length > 0 && (
                  <div className="mitigation-strategies">
                    <h5>Stress Mitigation:</h5>
                    <ul>
                      {suggestion.experienceImpact.mitigationStrategies.map((strategy, index) => (
                        <li key={index}>{strategy}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Alternative Solutions */}
      {suggestion.alternatives && suggestion.alternatives.length > 0 && (
        <div className="impact-section">
          <h3 className="section-title">
            <span className="section-number">💡</span>
            Alternative Approaches
          </h3>
          <div className="section-content">
            <div className="alternatives-list">
              {suggestion.alternatives.map((alternative, index) => (
                <div key={index} className="alternative-card">
                  <h4 className="alternative-title">{alternative.title}</h4>
                  <p className="alternative-description">{alternative.description}</p>
                  <div className="alternative-pros-cons">
                    <div className="pros">
                      <h5>Pros:</h5>
                      <ul>
                        {alternative.pros.map((pro, i) => (
                          <li key={i}>{pro}</li>
                        ))}
                      </ul>
                    </div>
                    <div className="cons">
                      <h5>Cons:</h5>
                      <ul>
                        {alternative.cons.map((con, i) => (
                          <li key={i}>{con}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default ImpactAnalysis